import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLanguage } from "./LanguageContext";
import { Button } from "./ui/button";
import { TablePagination } from "./ui/table-pagination";
import { db, Fee } from "../lib/supabase";
import { KpiCard } from "./KpiCard";

const FeesIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-[#F5F6FA] flex items-center justify-center">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8" />
    </svg>
  </div>
);

export function Fees() {
  const { t } = useLanguage();
  const [fees, setFees] = useState<Fee[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [total, setTotal] = useState(0);
  const [estadoFilter, setEstadoFilter] = useState<string>("");
  const [feesTotal, setFeesTotal] = useState<number>(0);
  const [feesDelta, setFeesDelta] = useState<{ value: string; type: "positive" | "negative" | "neutral"; label: string } | null>(null);
  // compute sorted list and page sum
  const sortedFees = useMemo(() => {
    if (!sortBy) return [...fees];
    const list = [...fees];
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a: Fee, b: Fee) => {
      switch (sortBy) {
        case 'concepto':
          return dir * String(a.concepto || '').localeCompare(String(b.concepto || ''), undefined, { sensitivity: 'base' });
        case 'cantidad':
          return dir * ((Number(a.cantidad) || 0) - (Number(b.cantidad) || 0));
        case 'fecha':
          return dir * (new Date(a.fecha || 0).getTime() - new Date(b.fecha || 0).getTime());
        case 'fecha_pago':
          return dir * ((a.fecha_pago ? new Date(a.fecha_pago).getTime() : 0) - (b.fecha_pago ? new Date(b.fecha_pago).getTime() : 0));
        case 'estado':
          return dir * String(a.estado || '').localeCompare(String(b.estado || ''), undefined, { sensitivity: 'base' });
        default:
          return 0;
      }
    });
    return list;
  }, [fees, sortBy, sortDir]);

  const pageSum = sortedFees.reduce((s, f) => s + Number(f.cantidad || 0), 0);

  const tableRef = useRef<HTMLTableElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const topInnerRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [page, pageSize, estadoFilter]);

  useEffect(() => {
    const loadFeesTotals = async () => {
      try {
        const [allTotal, quarterRes] = await Promise.all([
          db.getFeesTotal(),
          db.getFeesTotalsByQuarter(undefined, true),
        ] as any);

        const totalAll = Number(allTotal || 0);
        const current = Number(quarterRes?.currentTotal || 0);
        const previous = Number(quarterRes?.previousTotal || 0);

        setFeesTotal(totalAll);

        let deltaValue = "";
        let deltaType: "positive" | "negative" | "neutral" = "neutral";

        if (previous === 0) {
          if (current === 0) {
            deltaValue = "0%";
            deltaType = "neutral";
          } else {
            deltaValue = "N/A";
            deltaType = "neutral";
          }
        } else {
          const pct = ((current - previous) / previous) * 100;
          const sign = pct > 0 ? "+" : pct < 0 ? "" : "";
          deltaValue = `${sign}${pct.toFixed(1)}%`;
          deltaType = pct > 0 ? "positive" : pct < 0 ? "negative" : "neutral";
        }

        setFeesDelta({ value: deltaValue, type: deltaType, label: t("time.quarter") });
      } catch (e) {
        console.error("Error cargando totales de fees:", e);
      }
    };
    loadFeesTotals();
  }, [t]);

  async function fetchPage() {
    try {
      setLoading(true);
      const res = await db.getFeesPaged(page, pageSize, estadoFilter);
      setFees(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error("getFeesPaged error:", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const syncWidths = () => {
      const table = tableRef.current;
      const topInner = topInnerRef.current;
      const bottom = bottomWrapperRef.current;
      if (!table || !topInner || !bottom) return;
      topInner.style.width = `${table.scrollWidth}px`;
      const isMobile = window.innerWidth < 768;
      setShowScrollHint(isMobile || bottom.scrollWidth > bottom.clientWidth);
    };

    syncWidths();
    window.addEventListener("resize", syncWidths);

    const top = topScrollerRef.current;
    const bottom = bottomWrapperRef.current;
    if (top && bottom) {
      const onTopScroll = () => { if (bottom) bottom.scrollLeft = top.scrollLeft; };
      const onBottomScroll = () => { if (top) top.scrollLeft = bottom.scrollLeft; };
      top.addEventListener("scroll", onTopScroll);
      bottom.addEventListener("scroll", onBottomScroll);
      return () => {
        top.removeEventListener("scroll", onTopScroll);
        bottom.removeEventListener("scroll", onBottomScroll);
        window.removeEventListener("resize", syncWidths);
      };
    }

    return () => window.removeEventListener("resize", syncWidths);
  }, [fees.length]);

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

  return (
    <div className="space-y-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl" style={{ fontWeight: 600 }}>Fees</h1>
          <p className="text-gray-600">Gestión de cargos y comisiones</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">{t("fees.filter.status")}:</label>
              <select
                className="h-8 border rounded px-2 bg-white"
                value={estadoFilter}
                onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
              >
                <option value="">{t("fees.filter.all")}</option>
                <option value="PAID">{t("fees.filter.paid")}</option>
                <option value="PENDING">{t("fees.filter.pending")}</option>
              </select>
            </div>
            <div style={{ minWidth: 240 }} className="hidden" />
            <div>
              <Button variant="black" className="invisible h-10 px-4">Nuevo fee</Button>
            </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg">
        {showScrollHint && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2 md:hidden transition-opacity duration-300 opacity-100">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
              <path d="M3 12h18" />
              <path d="M7 6L3 12L7 18" />
              <path d="M17 6L21 12L17 18" />
            </svg>
            <span>Desliza horizontalmente</span>
          </div>
        )}
        <div ref={topScrollerRef} className="overflow-x-auto overflow-y-hidden" style={{ height: 16 }}>
          <div ref={topInnerRef} style={{ height: 1 }} />
        </div>
        <div ref={bottomWrapperRef} className="overflow-x-auto fees-wrapper w-full rounded-lg bg-white">
          <style>{`
            /* Fees table styles copied from FeesManager for consistent layout */
            .fees-table { width: 100%; table-layout: fixed; }
            .fees-colgroup col { width: auto; }

            @media (max-width: 640px) {
              /* On small screens keep table layout and let the wrapper handle horizontal scroll */
              .fees-table { width: 100%; table-layout: fixed; }
              .fees-table thead { display: table-header-group; }
              .fees-table tbody tr { display: table-row; }
              .fees-table tbody tr > td { display: table-cell; padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
              .fees-table th, .fees-table td { white-space: nowrap; }
              .fees-colgroup col.col-cantidad, .fees-colgroup col.col-fecha, .fees-colgroup col.col-fechaPago, .fees-colgroup col.col-estado { display: table-column; width: auto !important; }
            }

            /* Scroll container behaviour: wrapper handles scroll and fits parent width */
            .fees-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100vw; box-sizing: border-box; }

            /* Use table-level horizontal scrolling inside the wrapper; do not truncate content */
            .fees-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100vw; box-sizing: border-box; }
            .fees-table { width: max-content; min-width: 100%; table-layout: auto; }
            /* Force single-line cells so the table can grow horizontally and the wrapper shows an internal scrollbar */
            .fees-table th, .fees-table td { white-space: nowrap; overflow: visible; text-overflow: clip; }

            /* On very narrow screens allow cells to wrap rather than expanding the table */
            @media (max-width: 480px) {
              .fees-table th, .fees-table td { white-space: normal; }
            }

            @media (min-width: 768px) {
              .fees-colgroup col.col-concepto { width: 36% !important; }
              .fees-colgroup col.col-cantidad { width: 16% !important; }
              .fees-colgroup col.col-fecha { width: 12% !important; }
              .fees-colgroup col.col-fechaPago { width: 12% !important; }
              .fees-colgroup col.col-estado { width: 12% !important; }
              .fees-colgroup col.col-link { width: 12% !important; }
            }

            .fees-table th, .fees-table td { min-width: 0; max-width: 100%; padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
            .fees-table thead th { text-align: left !important; border-bottom: 1px solid #f3f4f6; }
            .fees-table td .inline-flex { gap: 0.5rem; }
            .fees-table thead th:last-child, .fees-table td[data-label="Acciones"] { min-width: 120px !important; box-sizing: border-box; }
            .fees-table { border-collapse: collapse; }
            .fees-table th, .fees-table td { background-clip: padding-box; }
            /* Ensure paddings don't increase layout width unexpectedly */
            .fees-table, .fees-table th, .fees-table td { box-sizing: border-box; }

            /* Prevent the page content from overflowing the viewport similar to applying this to main */
            main { max-width: 100vw; box-sizing: border-box; overflow-x: hidden; }
            .fees-table tbody tr:hover td { background-color: #F9FAFB; transition: background-color 120ms ease; }
          `}</style>

          <table ref={tableRef} className="table-auto divide-y divide-gray-100 fees-table">
            <colgroup className="fees-colgroup">
              <col className="col-concepto" />
              <col className="col-cantidad" />
              <col className="col-fecha" />
              <col className="col-fechaPago" />
              <col className="col-estado" />
              <col className="col-link" />
            </colgroup>
            <thead>
              <tr className="bg-white">
                  <th onClick={() => { setSortBy('concepto'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Concepto {sortBy==='concepto' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                  <th onClick={() => { setSortBy('cantidad'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Cantidad {sortBy==='cantidad' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                  <th onClick={() => { setSortBy('fecha'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Fecha {sortBy==='fecha' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                  <th onClick={() => { setSortBy('fecha_pago'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Fecha pago {sortBy==='fecha_pago' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                  <th onClick={() => { setSortBy('estado'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Estado {sortBy==='estado' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                  <th className="text-left px-6 py-4 text-xs text-gray-700 uppercase" style={{ fontWeight: 700 }}>Enlace</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td className="px-6 py-6" colSpan={6}>Cargando...</td>
                </tr>
              ) : fees.length === 0 ? (
                <tr>
                  <td className="px-6 py-6" colSpan={6}>No hay fees</td>
                </tr>
              ) : (
                <>
                  {sortedFees.map((f) => (
                    <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                      <td title={String(f.concepto)} className="px-6 py-4 text-sm font-semibold">{f.concepto}</td>
                      <td title={String(f.cantidad)} className="px-6 py-4 text-sm font-medium">{formatCurrency(Number(f.cantidad))}</td>
                      <td title={String(f.fecha)} className="px-6 py-4 text-sm">{f.fecha}</td>
                      <td title={String(f.fecha_pago || "-")} className="px-6 py-4 text-sm">{f.fecha_pago || "-"}</td>
                      <td title={String(f.estado)} className="px-6 py-4 text-sm">{(() => {
                        const isPaid = String(f.estado || "").toUpperCase() === "PAID";
                        const baseClasses = "inline-block text-sm px-3 py-1 rounded-full";
                        const paidClasses = "bg-black text-white";
                        const defaultClasses = "bg-gray-100 text-gray-800";
                        return <span className={`${baseClasses} ${isPaid ? paidClasses : defaultClasses}`}>{f.estado}</span>;
                      })()}</td>
                      <td
                        title={String(f.link || "-")}
                        className="px-6 py-4 text-sm cursor-pointer"
                        onClick={() => {
                          if (!f.link) return;
                          try {
                            window.open(String(f.link), "_blank", "noopener,noreferrer");
                          } catch (err) {
                            const w = window.open("about:blank", "_blank");
                            if (w) w.location.href = String(f.link);
                          }
                        }}
                      >
                        {f.link ? (
                          <a
                            href={String(f.link)}
                            onClick={(e) => { e.preventDefault(); if (f.link) window.open(String(f.link), "_blank", "noopener,noreferrer"); }}
                            className="text-sm text-blue-600 hover:underline"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Total row for the displayed fees */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-6 py-4 text-sm">TOTAL</td>
                    <td className="px-6 py-4 text-sm">{formatCurrency(pageSum)}</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span>{t("fees.pageSize.label")}: </span>
            <select
              className="h-8 border rounded px-2 bg-white"
              value={pageSize}
              onChange={(e) => { const s = Number(e.target.value); setPageSize(s); setPage(1); }}
            >
              {[10,20,50,100].map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <TablePagination
            page={page}
            totalPages={Math.max(1, Math.ceil(total / pageSize))}
            pageSize={pageSize}
            hidePageSizeSelector
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </div>
  );
}

export default Fees;
